import { useEffect, useState, FormEvent, ChangeEvent } from "react";
import {
  FiX,
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiInfo,
  FiUserPlus,
  FiLock,
  FiImage,
} from "react-icons/fi";
import students from "../../../services/students";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import utils from "../../../helpers/utils";
import { User } from "../../../types";
import ImageInput from "../../ui/ImageInput";

interface NewStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  student?: Partial<User> | null;
}

const initState = {
  name: "",
  photo: "",
  email: "",
  password: "",
  birth_date: "",
  phone: "",
  validity: "",
};

function NewStudentModal({
  isOpen,
  onClose,
  onSuccess = () => {},
  student,
}: NewStudentModalProps) {
  const [form, setForm] = useState(initState);
  const [loading, setLoading] = useState(false);
  const { getUser } = useAuth();

  async function handleResetPassword() {
    if (!student?.id) return;

    if (
      !window.confirm("Tem certeza que deseja redefinir a senha deste aluno?")
    ) {
      return;
    }

    setLoading(true);
    const success = await students.resetPassword(student.id);
    setLoading(false);

    if (success) {
      toast.success(
        "Senha redefinida com sucesso! Nova senha enviada por e-mail.",
      );
    } else {
      toast.error("Erro ao redefinir senha.");
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);

    const user = getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    const payload: Partial<User> & {
      password?: string;
      validity?: string;
      professional_id?: number;
    } = {
      name: form.name,
      email: form.email,
      birth_date: form.birth_date,
      phone: form.phone.replace(/\D/g, ""),
      validity: form.validity,
      professional_id: user.id,
      photo: form.photo,
    };

    if (form.password) {
      payload.password = form.password;
    }

    let data;

    if (student?.id) {
      data = await students.updateProfile(student.id, payload);
    } else {
      payload.password = form.password; // Ensure password is sent for create
      data = await students.create(payload as unknown as Partial<User>);
    }

    setLoading(false);

    if (data) {
      toast.success(
        student
          ? "Aluno atualizado com sucesso!"
          : "Aluno cadastrado com sucesso! A senha foi enviada por e-mail.",
      );
      onSuccess();
      onClose();
    } else {
      toast.error(
        student
          ? "Houve um problema ao atualizar o aluno."
          : "Houve um problema ao cadastrar o aluno, por favor, tente novamente.",
      );
    }
  }

  useEffect(() => {
    if (isOpen) {
      if (student) {
        setForm({
          name: student.name || "",
          photo: student.photo || "",
          email: student.email || "",
          password: "",
          birth_date: student.birth_date?.split("T")[0] || "",
          phone: student.phone || "",
          validity: student.user_validity?.split("T")[0] || "",
        });
      } else {
        setForm(initState);
      }
    }
  }, [isOpen, student]);

  const handleChange = (target: { name: string; value: string }) => {
    if (target.name === "phone") {
      const digitsOnly = target.value.replace(/\D/g, "");
      setForm({ ...form, phone: digitsOnly });
      return;
    }

    setForm({ ...form, [target.name]: target.value });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 p-0 sm:p-4 sm:backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] overflow-hidden sm:rounded-2xl border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark sm:my-auto flex flex-col">
        {/* Header Premium com Gradiente */}
        <div className="relative border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-3 sm:px-6 py-3 sm:py-4 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

          <button
            onClick={onClose}
            className="absolute right-2 top-2.5 sm:right-4 sm:top-4 z-10 rounded-xl p-1.5 sm:p-2 text-gray-500 outline-none transition-all hover:bg-white hover:text-gray-700 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-boxdark dark:hover:text-gray-200 touch-manipulation"
            aria-label="Fechar modal"
          >
            <FiX size={18} className="sm:w-6 sm:h-6" />
          </button>

          <div className="relative flex items-center gap-2.5 sm:gap-3 pr-10 sm:pr-0">
            <div className="rounded-xl bg-gradient-to-br from-purple-600 to-purple-600 p-2 sm:p-2.5 text-white shadow-sm flex-shrink-0">
              <FiUserPlus size={16} className="sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white leading-tight">
                {student ? "Editar Aluno" : "Cadastrar Novo Aluno"}
              </h3>
              <p className="mt-0.5 text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-tight">
                {student
                  ? "Edite os dados do aluno"
                  : "Preencha os dados para adicionar um novo aluno"}
              </p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <form
          onSubmit={submit}
          className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-6"
        >
          {/* Foto do Aluno */}
          <div className="flex justify-center mb-6">
            <div className="relative w-32 h-32">
              {form.photo && (
                <ImageInput
                  id="student-photo-input"
                  label=""
                  value={form.photo}
                  onChange={(val) => setForm({ ...form, photo: val as string })}
                  classNameImage="!w-32 !h-32 rounded-full object-cover border-4 border-white shadow-lg"
                  className="!w-32 !h-32 mx-auto"
                />
              )}
              {!form.photo && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = document.getElementById(
                      "student-photo-input",
                    ) as HTMLInputElement;
                    if (input) {
                      input.click();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = document.getElementById(
                        "student-photo-input",
                      ) as HTMLInputElement;
                      if (input) {
                        input.click();
                      }
                    }
                  }}
                  className="absolute inset-0 w-32 h-32 rounded-full border-4 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/90 dark:bg-gray-800/90 backdrop-blur-sm flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 hover:bg-purple-50/95 dark:hover:border-purple-500 dark:hover:bg-purple-900/40 transition-all group z-10 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                >
                  <FiImage
                    size={28}
                    className="text-gray-400 dark:text-gray-500 group-hover:text-purple-500 transition-colors mb-1"
                  />
                  <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 group-hover:text-purple-500 transition-colors text-center px-2">
                    Adicionar foto
                  </span>
                </div>
              )}
              {form.photo && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, photo: "" })}
                  className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-red-600 transition-colors z-10"
                  aria-label="Remover foto"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {/* Seção: Dados Pessoais */}
            <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-3 sm:p-5 shadow-sm dark:border-strokedark dark:bg-boxdark">
              <div className="mb-3 sm:mb-4 flex items-center gap-2">
                <div className="h-0.5 sm:h-1 w-6 sm:w-8 rounded bg-purple-600"></div>
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  Dados Pessoais
                </h4>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
                {/* Nome Completo */}
                <div className="sm:col-span-2">
                  <label
                    htmlFor="name"
                    className="mb-1.5 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Nome Completo <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                      <FiUser size={14} className="sm:w-[18px] sm:h-[18px]" />
                    </span>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={form.name}
                      onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                        handleChange(target)
                      }
                      placeholder="Digite o nome completo"
                      className="w-full max-w-full rounded-lg sm:rounded-xl border border-stroke bg-gray-50 py-2.5 sm:py-2.5 pl-9 sm:pl-11 pr-3 sm:pr-4 text-xs sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation box-border"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                      <FiMail size={14} className="sm:w-[18px] sm:h-[18px]" />
                    </span>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                        handleChange(target)
                      }
                      placeholder="exemplo@email.com"
                      className="w-full max-w-full rounded-lg sm:rounded-xl border border-stroke bg-gray-50 py-2.5 sm:py-2.5 pl-9 sm:pl-11 pr-3 sm:pr-4 text-xs sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation box-border"
                    />
                  </div>
                </div>

                {/* Telefone */}
                <div>
                  <label
                    htmlFor="phone"
                    className="mb-1.5 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Telefone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                      <FiPhone size={14} className="sm:w-[18px] sm:h-[18px]" />
                    </span>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      maxLength={16}
                      required
                      value={
                        form.phone ? utils.formatarTelefone(form.phone) : ""
                      }
                      onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                        handleChange(target)
                      }
                      placeholder="(00) 00000-0000"
                      className="w-full max-w-full rounded-lg sm:rounded-xl border border-stroke bg-gray-50 py-2.5 sm:py-2.5 pl-9 sm:pl-11 pr-3 sm:pr-4 text-xs sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation box-border"
                    />
                  </div>
                </div>

                {/* Data de Nascimento */}
                <div>
                  <label
                    htmlFor="birth_date"
                    className="mb-1.5 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Data de Nascimento <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                      <FiCalendar
                        size={14}
                        className="sm:w-[18px] sm:h-[18px]"
                      />
                    </span>
                    <input
                      type="date"
                      id="birth_date"
                      name="birth_date"
                      required
                      value={form.birth_date}
                      onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                        handleChange(target)
                      }
                      className="w-full max-w-full rounded-lg sm:rounded-xl border border-stroke bg-gray-50 py-2.5 sm:py-2.5 pl-9 sm:pl-11 pr-2 sm:pr-4 text-xs sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation box-border"
                      style={{ WebkitAppearance: "none" }}
                    />
                  </div>
                </div>

                {/* Validade do Plano */}
                <div>
                  <label
                    htmlFor="validity"
                    className="mb-1.5 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Validade do Plano <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                      <FiCalendar
                        size={14}
                        className="sm:w-[18px] sm:h-[18px]"
                      />
                    </span>
                    <input
                      type="date"
                      id="validity"
                      name="validity"
                      required
                      value={form.validity}
                      onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                        handleChange(target)
                      }
                      className="w-full max-w-full rounded-lg sm:rounded-xl border border-stroke bg-gray-50 py-2.5 sm:py-2.5 pl-9 sm:pl-11 pr-2 sm:pr-4 text-xs sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation box-border"
                      style={{ WebkitAppearance: "none" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box Premium */}
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-50 p-3 sm:p-4 dark:border-purple-800 dark:from-purple-900/20 dark:to-purple-900/20">
              <div className="absolute right-0 top-0 h-16 sm:h-24 w-16 sm:w-24 bg-gradient-to-br from-purple-500/10 to-purple-500/10 blur-sm"></div>

              <div className="relative flex items-start gap-2.5 sm:gap-3">
                <div className="rounded-lg sm:rounded-xl bg-purple-100 p-2 sm:p-2.5 dark:bg-purple-900/40 flex-shrink-0">
                  <FiInfo
                    className="text-purple-600 dark:text-purple-400"
                    size={18}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="mb-1 sm:mb-1 font-bold text-xs sm:text-sm text-purple-900 dark:text-purple-300">
                    Informação Importante
                  </h5>
                  <p className="text-xs sm:text-sm leading-relaxed text-purple-800 dark:text-purple-400">
                    Após o cadastro, uma senha temporária será gerada
                    automaticamente e enviada para o e-mail do aluno. O aluno
                    poderá alterar a senha no primeiro acesso ao sistema.
                  </p>
                  <div className="mt-2 sm:mt-2.5 flex flex-wrap gap-1.5 sm:gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ Senha automática
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ E-mail de boas-vindas
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ Acesso imediato
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer Sticky com Botões */}
        <div className="sticky bottom-0 border-t border-stroke bg-white px-3 sm:px-6 py-3 sm:py-2 dark:border-strokedark dark:bg-boxdark shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] sm:shadow-none">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <p className="hidden sm:block text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Todos os campos marcados com{" "}
              <span className="text-red-500">*</span> são obrigatórios
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5 w-full sm:w-auto">
              {student?.id && (
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={loading}
                  className="w-full sm:w-auto order-3 sm:order-1 rounded-lg sm:rounded-xl border-2 border-orange-500 px-4 sm:px-5 py-2.5 sm:py-2.5 text-sm sm:text-base font-semibold text-orange-500 transition-all hover:bg-orange-50 disabled:opacity-50 dark:text-orange-400 dark:hover:bg-orange-900/20 touch-manipulation active:scale-95 sm:active:scale-100 mr-auto"
                >
                  <FiLock size={18} className="mr-2 inline" />
                  Redefinir Senha
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="w-full sm:w-auto order-2 sm:order-1 rounded-lg sm:rounded-xl border-2 border-stroke px-4 sm:px-5 py-2.5 sm:py-2.5 text-sm sm:text-base font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4 touch-manipulation active:scale-95 sm:active:scale-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                onClick={submit}
                className="w-full sm:w-auto order-1 sm:order-2 inline-flex items-center justify-center gap-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-purple-600 to-purple-600 px-5 sm:px-6 py-2.5 sm:py-2.5 text-sm sm:text-base font-semibold text-white shadow-sm transition-all hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:hover:scale-100 touch-manipulation active:scale-95 sm:active:scale-105"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 sm:h-5 sm:w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <FiUserPlus size={18} className="sm:w-5 sm:h-5" />
                    Cadastrar Aluno
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewStudentModal;
